import React, { useContext, useEffect, useState } from 'react';
import { arrayOf, func, number, shape, string } from 'prop-types';
import { Box, Typography } from '@mui/material';
import * as R from 'ramda';

// Components
import { MAutocomplete, MDropdown } from 'common';

// Contexts
import { AuthContext } from 'authentication/AuthContext';

// Utils
import { validateRequiredAutocomplete } from 'utils/helpers/formValidations';
import { SELF_AUTH_TYPE_OPTIONS, STEP_ONE_SUBHEADER } from 'feature/loading-management/utils/const';
import { useGetAffiliateOptions } from 'hooks';
import { capitalize } from 'utils/helpers/format';
import { findOptionByValue } from 'utils/helpers/function';
import { userRoles } from 'utils/currentUserRoles';

const DropDownSection = ({
  affiliate,
  authorizationBy,
  locationId,
  productId,
  selfAuthType,
  setHasUnsavedChanges,
  setParams,
  viewByListOptions,
}) => {
  const { currentAffiliate } = useContext(AuthContext);
  const userAffiliatesOptions = useGetAffiliateOptions([
    userRoles.SUPPLIER,
    userRoles.INVENTORY_OWNER,
  ]);

  useEffect(() => {
    if (R.isEmpty(affiliate)) {
      setParams({
        // the findOptionByValue function determines the affiliate
        // default selected in the Create Wizard
        // value is customerIdName-dataCompanyId
        affiliate: findOptionByValue(
          `${currentAffiliate?.customerIdName}-${currentAffiliate?.dataCompanyId}`,
          userAffiliatesOptions
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliate.value]);
  const [listOptionValidation, setListOptionValidation] = useState('');

  const valueToFind = authorizationBy === 'location' ? locationId : productId;
  // could be a list of products or locations
  // the findOptionByValue function determines the affiliate
  // default selected in the Create Wizard
  const selectedViewByListOption = findOptionByValue(valueToFind, viewByListOptions);

  // set affiliate/other params in createNewSelfAuth store
  // we set affiliate with the full userAffiliate obj in the store bc we need all the keys to make the API call
  // we only need the value itself for the other keys
  const handleOnChange = (name, value) => {
    setHasUnsavedChanges(true);
    return R.ifElse(
      R.equals('affiliate'),
      () => {
        setParams({
          locationId: undefined,
          [name]: findOptionByValue(value, userAffiliatesOptions),
          productId: undefined,
        });
      },
      () => {
        setParams({ [name]: value });
      }
    )(name);
  };

  // R.flip ensures value is passed to function rather than synthetic event
  const handleSetProductOrLocation = R.compose(
    setListOptionValidation,
    validateRequiredAutocomplete,
    R.tap(value => handleOnChange(`${authorizationBy}Id`, value)),
    R.prop('value')
  );

  return (
    <Box display="flex" flexDirection="column" py={1}>
      <Typography color="textPrimary" variant="body1">
        {STEP_ONE_SUBHEADER}
      </Typography>
      {userAffiliatesOptions.length > 1 && (
        <Box pt={3}>
          <MDropdown
            id="affiliate"
            items={userAffiliatesOptions}
            label="Affiliate"
            name="affiliate"
            onChange={({ name, value }) => handleOnChange(name, value)}
            styleProps={{
              menu: { transformOrigin: { horizontal: 'left', vertical: 'top' } },
            }}
            value={affiliate.value}
          />
        </Box>
      )}
      <Box py={2}>
        <MDropdown
          id="selfAuthType"
          items={SELF_AUTH_TYPE_OPTIONS}
          label="Self Authorization Type"
          name="selfAuthType"
          onChange={({ name, value }) => handleOnChange(name, value)}
          styleProps={{
            menu: { transformOrigin: { horizontal: 'left', vertical: 'top' } },
          }}
          value={selfAuthType}
        />
      </Box>

      <MAutocomplete
        error={!R.isEmpty(listOptionValidation)}
        helperText={listOptionValidation}
        id={authorizationBy}
        label={capitalize(authorizationBy)}
        loading={false}
        name={authorizationBy}
        onChange={R.flip(handleSetProductOrLocation)}
        openOnFocus
        options={viewByListOptions}
        value={selectedViewByListOption?.value}
      />
    </Box>
  );
};

DropDownSection.propTypes = {
  affiliate: shape({}),
  authorizationBy: string.isRequired,
  locationId: number,
  productId: number,
  selfAuthType: string,
  setHasUnsavedChanges: func.isRequired,
  setParams: func.isRequired,
  viewByListOptions: arrayOf(
    shape({
      id: number,
      nameLong: string,
      nameShort: string,
    })
  ),
};

DropDownSection.defaultProps = {
  affiliate: {},
  locationId: undefined,
  productId: undefined,
  selfAuthType: '',
  viewByListOptions: [],
};

export default DropDownSection;
